import api from "./api";

export type PeriodMode = "month" | "fy" | "range";

export interface PayrollFilters {
  mode: PeriodMode;
  year?: number;
  month?: number;
  financial_year?: string;
  start_date?: string;   // yyyy-MM-dd
  end_date?: string;
  department?: string;
}

function qs(f: PayrollFilters): string {
  const p = new URLSearchParams();
  p.set("mode", f.mode);
  if (f.year != null) p.set("year", String(f.year));
  if (f.month != null) p.set("month", String(f.month));
  if (f.financial_year) p.set("financial_year", f.financial_year);
  if (f.start_date) p.set("start_date", f.start_date);
  if (f.end_date) p.set("end_date", f.end_date);
  if (f.department) p.set("department", f.department);
  return p.toString();
}

// Filename-safe period, kept identical to period_slug() in
// hrms/utils/payroll_reports.py. The server's Content-Disposition name isn't
// readable here (CORS doesn't expose it), so the browser names the file itself.
export function periodSlug(f: PayrollFilters): string {
  if (f.mode === "fy") return `FY${f.financial_year || ""}`;
  if (f.mode === "range") return `${f.start_date || ""}_to_${f.end_date || ""}`;
  return `${f.year}-${String(f.month).padStart(2, "0")}`;
}

function saveBlob(data: BlobPart, filename: string, type?: string) {
  const url = window.URL.createObjectURL(new Blob([data], type ? { type } : undefined));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export const payrollReportsApi = {
  scope: () => api.get(`/salary/reports/scope`),
  overview: (f: PayrollFilters) => api.get(`/salary/reports/overview?${qs(f)}`),
  register: (f: PayrollFilters) => api.get(`/salary/reports/register?${qs(f)}`),
  registerDaily: (emptId: string, year: number, month: number) =>
    api.get(`/salary/reports/register/${emptId}/daily?year=${year}&month=${month}`),
  components: (f: PayrollFilters) => api.get(`/salary/reports/components?${qs(f)}`),
  statutory: (f: PayrollFilters) => api.get(`/salary/reports/statutory?${qs(f)}`),
  approvals: (f: PayrollFilters) => api.get(`/salary/reports/approvals?${qs(f)}`),
  retainer: (f: PayrollFilters) => api.get(`/salary/reports/retainer?${qs(f)}`),
  leaveImpact: (f: PayrollFilters) => api.get(`/salary/reports/leave-impact?${qs(f)}`),
  adjustments: (f: PayrollFilters) => api.get(`/salary/reports/adjustments?${qs(f)}`),
  reimbursements: (f: PayrollFilters) => api.get(`/salary/reports/reimbursements?${qs(f)}`),
  fullFinal: (f: PayrollFilters) => api.get(`/salary/reports/full-final?${qs(f)}`),

  exportCsv: async (tab: string, f: PayrollFilters) => {
    const res = await api.get(`/salary/reports/export/csv?tab=${tab}&${qs(f)}`, {
      responseType: "blob",
    });
    saveBlob(res.data, `payroll-${tab}-${periodSlug(f)}.csv`);
  },

  exportPdf: async (f: PayrollFilters) => {
    const res = await api.get(`/salary/reports/export/pdf?${qs(f)}`, { responseType: "blob" });
    saveBlob(res.data, `payroll-register-${periodSlug(f)}.pdf`, "application/pdf");
  },
};
